import React from 'react';
import Button from '../../components/Button/Button';
import { PortalWithState } from 'react-portal';
import TestAnniversaryModal from './TestAnniversaryModal';
import SendNoticeModal from './SendNoticeModal';
import useMode from '../../hooks/useMode';
import { IdArrayInput } from '../../hooks/useTablePages';
import AddAssemblyJobsModal from '../../components/BackflowAssembly/AddAssemblyJobsModal';
import useSessionInfo from '../../hooks/useSessionInfo';
import { PREMIUM_COMPANY_IDS } from '../../constants';
import RequirePerms from '@/components/RequirePerms';
import BulkTagModal from '../../components/TagPicker/BulkTagModal';

type AssembliesActionsProps = { selectedIds: IdArrayInput };

const AssembliesActions: React.FC<AssembliesActionsProps> = ({
  selectedIds,
}): JSX.Element => {
  const mode = useMode();
  const { session } = useSessionInfo();

  const isPremiumCompany =
    session?.companyProfile?.id &&
    PREMIUM_COMPANY_IDS.includes(session.companyProfile.id);
  const canCreateJobs =
    (mode === 'company' && isPremiumCompany) || mode === 'water-supplier';
  const canSendNotice =
    (mode === 'company' && isPremiumCompany) || mode === 'water-supplier';

  return (
    <>
      {canCreateJobs && (
        <RequirePerms jobs="CREATE">
          <PortalWithState closeOnOutsideClick closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  variant="outline"
                  disabled={!selectedIds.length}
                  icon="add-new"
                  onClick={openPortal}
                >
                  Create Jobs
                </Button>
                {portal(
                  <AddAssemblyJobsModal
                    backflowAssemblyIds={selectedIds}
                    onClose={closePortal}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </RequirePerms>
      )}

      {canSendNotice && (
        <RequirePerms notices="CREATE">
          <PortalWithState closeOnOutsideClick closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  variant="outline"
                  disabled={!selectedIds.length}
                  icon="mail"
                  onClick={openPortal}
                >
                  Send Notice
                </Button>
                {portal(
                  <SendNoticeModal
                    selectedIds={selectedIds}
                    onClose={closePortal}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </RequirePerms>
      )}

      {mode === 'water-supplier' && (
        <RequirePerms assemblies="UPDATE">
          <PortalWithState closeOnOutsideClick closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  variant="outline"
                  disabled={!selectedIds.length}
                  icon="calendar"
                  onClick={openPortal}
                >
                  Set Test Anniversary
                </Button>
                {portal(
                  <TestAnniversaryModal
                    selectedIds={selectedIds}
                    onClose={closePortal}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </RequirePerms>
      )}

      {mode === 'water-supplier' && (
        <RequirePerms assemblies="UPDATE">
          <PortalWithState closeOnOutsideClick closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  variant="outline"
                  disabled={!selectedIds.length}
                  icon="plus-circle"
                  onClick={openPortal}
                >
                  Add Tags
                </Button>
                {portal(
                  <BulkTagModal
                    selectedIds={selectedIds}
                    objectType="assembly"
                    onClose={closePortal}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </RequirePerms>
      )}
    </>
  );
};

export default AssembliesActions;
